import { Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import { DeliveryNotesV3Service } from 'api-v3/services';
import {
    ConsigneeDeliveryNotesArrayV3,
    SortType,
    DeliveryNotesFilterProperties,
} from 'api-v3/models';
import { UserRoles } from 'api/models';
import {
    setLoading,
    setError,
    setDeliveryNotes,
    isLoading,
} from '@app/stores/deliveryNotesStore';
import { DeliveryNoteData } from '@app/shared/models/store-types';
import {
    searchState,
    updatePaginationFromApiResponse,
    resetPaginationToEmpty,
    cancelOngoingRequests,
} from '@app/stores/searchStore';
import { ABORT_SIGNAL } from '@app/shared/constants/http-context-tokens';

@Injectable({
    providedIn: 'root',
})
export class ConsigneeDeliveryNotesPaginatedService {
    constructor(private deliveryNotesV3Service: DeliveryNotesV3Service) {}

    async loadConsigneeDeliveryNotesPaginated(
        organizationSiteKey: string
    ): Promise<void> {
        // Prevent duplicate calls if already loading
        if (isLoading()) {
            return;
        }

        setLoading(true);
        setError(null);

        // Cancel any ongoing requests and get new abort signal
        const abortSignal = cancelOngoingRequests();

        try {
            const currentSearchState = searchState();

            // Get site information for role-based read_only logic, selected locations, and showClosed filter
            const {
                getCurrentOrganizationSite,
                getSelectedLocationKeys,
                getShowClosed,
            } = await import('@app/stores/appStore');
            const currentSite = getCurrentOrganizationSite();
            const selectedSiteKeys = getSelectedLocationKeys('consignee');
            const showClosed = getShowClosed();

            // Don't make API call if no organization sites are selected
            if (selectedSiteKeys.length === 0) {
                return;
            }

            // Check if request was cancelled before proceeding
            if (abortSignal?.aborted) {
                return;
            }

            // hack search-text for boolean search-strings
            let searchText = currentSearchState.searchText;
            if (
                currentSearchState.searchText &&
                (currentSearchState.searchText === 'Ja' ||
                    currentSearchState.searchText === 'Yes')
            ) {
                searchText = 'true';
            } else if (
                currentSearchState.searchText &&
                (currentSearchState.searchText === 'Nein' ||
                    currentSearchState.searchText === 'No')
            ) {
                searchText = 'false';
            }

            const sortBy = currentSearchState.sort.sortBy;

            // Use valid sort field or fall back to default - ensure type safety
            const validSortBy = sortBy as any;

            // Default to desc for dateCreated (newest first), use user's direction for other fields
            const sortDirection =
                validSortBy === 'dateCreated' && !sortBy
                    ? SortType.Desc // Default to newest first when no sort is specified
                    : currentSearchState.sort.sortDirection === 'asc'
                      ? SortType.Asc
                      : SortType.Desc;

            // Handle uppercase conversion for specific filter properties
            let filterValue = searchText || undefined;
            const filterProperty =
                currentSearchState.selectedFilterKey?.keyName;

            if (
                filterValue &&
                filterProperty &&
                (filterProperty === 'bundleNumber' ||
                    filterProperty === 'tourNumber')
            ) {
                filterValue = filterValue.toUpperCase();
            }

            let params = {
                organizationSiteKey,
                organizationSiteKeys:
                    selectedSiteKeys.length > 0 ? selectedSiteKeys : undefined,
                limit: currentSearchState.pagination.limit,
                offset: currentSearchState.pagination.offset,
                sort: sortDirection,
                sortBy: validSortBy,
                filterProperty: filterProperty
                    ? (filterProperty as DeliveryNotesFilterProperties)
                    : undefined,
                filterValue: filterValue,
                filterNotClosed: !showClosed,
                filterOnlyDiscrepancies: undefined,
            };

            // handle special-case "discrepancies" in filter
            if (filterProperty === 'discrepancies') {
                params = {
                    ...params,
                    filterOnlyDiscrepancies: filterValue,
                    filterProperty: undefined as any,
                    filterValue: undefined,
                };
            }

            // Check if request was already cancelled before making API call
            if (abortSignal?.aborted) {
                return;
            }

            // Create HttpContext with abort signal for proper HTTP request cancellation
            const context = new HttpContext().set(ABORT_SIGNAL, abortSignal);

            // This will go through the HTTP interceptors and can be cancelled
            const response = await lastValueFrom(
                this.deliveryNotesV3Service.getConsigneeDeliveryNotesV3(
                    params,
                    context
                )
            );

            if (response && response.data) {
                // Map the response to include orgSiteKey and ensure proper typing
                const notesWithOrgSiteKey: DeliveryNoteData[] =
                    response.data.map(note => ({
                        ...note,
                        orgSiteKey: organizationSiteKey,
                        read_only: currentSite
                            ? currentSite.roles.includes(
                                  UserRoles.RemoteConsignee
                              ) &&
                              !currentSite.roles.includes(UserRoles.Consignee)
                            : false,
                    }));

                setDeliveryNotes(notesWithOrgSiteKey);

                // Update pagination info in centralized searchStore
                updatePaginationFromApiResponse(response.paginationInfo);
            }
        } catch (error: any) {
            // Handle cancelled requests gracefully
            if (
                error?.name === 'AbortError' ||
                error?.message === 'Subscription has been cancelled' ||
                abortSignal?.aborted
            ) {
                return;
            }

            // Extract request ID from error if available
            const requestId =
                error?.headers?.get?.('x-request-id') ||
                error?.error?.requestId ||
                null;
            const errorMessage = requestId
                ? `GENERAL.ERRORS.FAILED_TO_LOAD_CONSIGNEE_DELIVERY_NOTES|${requestId}`
                : 'GENERAL.ERRORS.FAILED_TO_LOAD_CONSIGNEE_DELIVERY_NOTES';

            setError(errorMessage);

            // Reset pagination to empty state on error
            resetPaginationToEmpty();
        } finally {
            // Only set loading to false if request wasn't cancelled
            if (!abortSignal?.aborted) {
                setLoading(false);
            }
        }
    }
}
